import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { BACKEND_URL, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ScopedLibrary } from "@/components/ScopedLibrary";
import { MaterialPreview } from "@/components/MaterialPreview";
import { EmptyState, fmtDate, fmtSize } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Upload, Search, FileText, Download, Eye, Archive, Trash2, RefreshCw, Loader2,
  Link2, ExternalLink, FolderTree, Inbox, FilePlus2, Building2, Settings2, X, Plus, FileCheck2, ListChecks,
} from "lucide-react";

const DEFAULT_CATEGORIES = ["Onboarding", "SOPs", "Training Packets"];
const isUnfiled = (m) => !m.position || !m.category;

export default function Library() {
  const { user } = useAuth();
  const isAdminView = ["admin", "manager", "hr"].includes(user?.role);

  // Trainers (and any non-admin) get the position-scoped view.
  if (!isAdminView) return (
    <div className="space-y-6" data-testid="library-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Training Library</h1>
        <p className="text-muted-foreground mt-1">SOPs, onboarding and training packets for your position.</p>
      </div>
      <ScopedLibrary />
    </div>
  );

  return <AdminLibrary />;
}

function AdminLibrary() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [structureOpen, setStructureOpen] = useState(false);
  const [fileMaterial, setFileMaterial] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [deleteMaterial, setDeleteMaterial] = useState(null);
  const [replaceId, setReplaceId] = useState(null);
  const replaceRef = useRef(null);

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const departments = settings?.departments || [];
  const categories = settings?.material_categories || DEFAULT_CATEGORIES;

  const params = { include_archived: includeArchived };
  if (q) params.q = q;
  const { data: materials = [], isLoading } = useQuery({
    queryKey: ["materials", params],
    queryFn: async () => (await api.get("/materials", { params })).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["materials"] });
  const archiveMutation = useMutation({
    mutationFn: async ({ id, val }) => api.patch(`/materials/${id}/archive`, { is_archived: val }),
    onSuccess: () => { invalidate(); toast.success("Updated"); },
  });
  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/materials/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });
  const replaceMutation = useMutation({
    mutationFn: async ({ id, file }) => { const fd = new FormData(); fd.append("file", file); return api.post(`/materials/${id}/replace`, fd); },
    onSuccess: () => { invalidate(); toast.success("File replaced — new version saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Replace failed"),
  });

  const download = async (id) => {
    try { const res = (await api.get(`/materials/${id}/download`)).data; window.open(res.external ? res.url : `${BACKEND_URL}${res.url}`, "_blank"); }
    catch { toast.error("Download failed"); }
  };
  const onReplacePick = (id) => { setReplaceId(id); replaceRef.current?.click(); };
  const onReplaceFile = (e) => { const f = e.target.files?.[0]; if (f && replaceId) replaceMutation.mutate({ id: replaceId, file: f }); e.target.value = ""; };

  const unfiled = materials.filter(isUnfiled);
  const forCell = (pos, cat) => materials.filter((m) => m.position === pos && m.category === cat);

  const Actions = ({ m }) => (
    <div className="flex gap-1 shrink-0">
      {m.source_type === "link" ? (
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => window.open(m.external_url, "_blank")} data-testid={`open-material-${m.id}`}><ExternalLink size={14} /></Button>
      ) : (
        <>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setPreviewId(m.id)} data-testid={`preview-material-${m.id}`}><Eye size={14} /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => download(m.id)} data-testid={`download-material-${m.id}`}><Download size={14} /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onReplacePick(m.id)} data-testid={`replace-material-${m.id}`}><RefreshCw size={14} /></Button>
        </>
      )}
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setFileMaterial(m)} data-testid={`file-material-${m.id}`} title="File / refile"><FolderTree size={14} /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: m.id, val: !m.is_archived })} data-testid={`archive-material-${m.id}`}><Archive size={14} /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteMaterial(m)} data-testid={`delete-material-${m.id}`}><Trash2 size={14} /></Button>
    </div>
  );

  const Row = ({ m }) => (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/50 ${m.is_archived ? "opacity-60" : ""}`} data-testid={`material-row-${m.id}`}>
      <div className="flex items-center justify-center h-8 w-8 rounded-md bg-accent/15 text-accent shrink-0">
        {m.source_type === "link" ? <Link2 size={15} /> : <FileText size={15} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate flex items-center gap-1.5">{m.title}{m.is_archived && <Badge variant="outline" className="text-[9px]">Archived</Badge>}</div>
        <div className="text-[11px] text-muted-foreground font-mono uppercase truncate">{m.source_type === "link" ? "link" : `${m.file_extension} · ${fmtSize(m.file_size)}`} · {m.uploaded_by_name}</div>
      </div>
      <Actions m={m} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Training Library</h1>
          <p className="text-muted-foreground mt-1">Organized by Department › Position › Category.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role === "admin" && (
            <Button variant="outline" className="gap-2 h-11 rounded-xl" onClick={() => window.open(`${API}/admin/master-checklist.pdf`, "_blank")} data-testid="master-checklist-button"><ListChecks size={18} /> Feature Checklist (PDF)</Button>
          )}
          <Button variant="outline" className="gap-2 h-11 rounded-xl" onClick={() => window.open(`${API}/admin/change-summary.pdf`, "_blank")} data-testid="change-summary-button"><FileCheck2 size={18} /> Change Summary (PDF)</Button>
          <Button variant="outline" className="gap-2 h-11 rounded-xl" onClick={() => setStructureOpen(true)} data-testid="manage-structure-button"><Settings2 size={18} /> Manage Structure</Button>
          <Button variant="outline" className="gap-2 h-11 rounded-xl" onClick={() => setLinkOpen(true)} data-testid="add-link-button"><Link2 size={18} /> Add Drive Link</Button>
          <Button className="gap-2 h-11 rounded-xl" onClick={() => setUploadOpen(true)} data-testid="sop-upload-button"><Upload size={18} /> Upload Material</Button>
        </div>
      </div>

      <Card className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input placeholder="Search all materials by title, description, tags…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="library-search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="include-archived-toggle" />
          <Label className="text-sm text-muted-foreground whitespace-nowrap">Show archived</Label>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-5">
          {/* Unfiled */}
          <Card className="p-4 border-warning/40" data-testid="unfiled-section">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-warning/15 text-warning"><Inbox size={18} /></div>
              <div>
                <h3 className="font-heading font-bold leading-none">Unfiled</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Documents not yet placed under a Position + Category. Hidden from trainers & trainees until filed.</p>
              </div>
              <span className="ml-auto text-xs font-mono text-muted-foreground">{unfiled.length}</span>
            </div>
            {unfiled.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-4">Everything is filed. 🎉</p>
            ) : (
              <div className="space-y-1">{unfiled.map((m) => <Row key={m.id} m={m} />)}</div>
            )}
          </Card>

          {/* Department tree */}
          {departments.length === 0 ? (
            <Card><EmptyState icon={Building2} title="No departments yet" desc="Use Manage Structure to add departments and positions." /></Card>
          ) : (
            <Accordion type="multiple" className="space-y-3" data-testid="department-tree">
              {departments.map((dept) => {
                const deptCount = materials.filter((m) => (dept.positions || []).includes(m.position)).length;
                return (
                  <Card key={dept.name} className="overflow-hidden">
                    <AccordionItem value={dept.name} className="border-0">
                      <AccordionTrigger className="px-4 hover:no-underline" data-testid={`dept-${dept.name}`}>
                        <div className="flex items-center gap-2.5">
                          <Building2 size={18} className="text-accent" />
                          <span className="font-heading font-bold">{dept.name}</span>
                          <Badge variant="secondary" className="text-[10px]">{deptCount}</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        {(dept.positions || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground py-2">No positions yet. Add positions in Manage Structure.</p>
                        ) : (
                          <Accordion type="multiple" className="space-y-2">
                            {dept.positions.map((pos) => {
                              const posCount = materials.filter((m) => m.position === pos).length;
                              return (
                                <AccordionItem key={pos} value={`${dept.name}:${pos}`} className="border rounded-xl overflow-hidden">
                                  <AccordionTrigger className="px-3 py-2.5 hover:no-underline text-sm" data-testid={`position-${pos}`}>
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold">{pos}</span>
                                      <Badge variant="outline" className="text-[10px]">{posCount}</Badge>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="px-3 pb-3 space-y-3">
                                    {categories.map((cat) => {
                                      const items = forCell(pos, cat);
                                      return (
                                        <div key={cat} data-testid={`cell-${pos}-${cat}`}>
                                          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                                            {cat} <span className="font-mono">({items.length})</span>
                                          </div>
                                          {items.length === 0 ? (
                                            <p className="text-xs text-muted-foreground/70 px-3 py-2 border border-dashed rounded-lg">No documents.</p>
                                          ) : (
                                            <div className="space-y-1">{items.map((m) => <Row key={m.id} m={m} />)}</div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Card>
                );
              })}
            </Accordion>
          )}
        </div>
      )}

      <input type="file" ref={replaceRef} className="hidden" onChange={onReplaceFile} />
      <MaterialDialog mode="upload" open={uploadOpen} onOpenChange={setUploadOpen} departments={departments} categories={categories} settings={settings} onDone={invalidate} />
      <MaterialDialog mode="link" open={linkOpen} onOpenChange={setLinkOpen} departments={departments} categories={categories} settings={settings} onDone={invalidate} />
      <FileDialog material={fileMaterial} onOpenChange={(o) => !o && setFileMaterial(null)} departments={departments} categories={categories} onDone={invalidate} />
      <StructureDialog open={structureOpen} onOpenChange={setStructureOpen} departments={departments} onDone={() => qc.invalidateQueries({ queryKey: ["settings"] })} />
      <MaterialPreview materialId={previewId} open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)} />

      <Dialog open={!!deleteMaterial} onOpenChange={(o) => !o && setDeleteMaterial(null)}>
        <DialogContent data-testid="delete-material-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete this document?</DialogTitle>
            <DialogDescription>This permanently removes "{deleteMaterial?.title}" from the library. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteMaterial(null)}>Cancel</Button>
            <Button variant="destructive" className="gap-2" disabled={deleteMutation.isPending}
              onClick={() => { deleteMutation.mutate(deleteMaterial.id); setDeleteMaterial(null); }} data-testid="confirm-delete-material">
              {deleteMutation.isPending && <Loader2 size={16} className="animate-spin" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- File / refile a material to Position + Category ---- */
function FileDialog({ material, onOpenChange, departments, categories, onDone }) {
  const open = !!material;
  const findDept = (pos) => departments.find((d) => (d.positions || []).includes(pos))?.name || "";
  const [dept, setDept] = useState("");
  const [position, setPosition] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (material) { setPosition(material.position || ""); setCategory(material.category || ""); setDept(findDept(material.position)); }
  }, [material]); // eslint-disable-line

  const deptPositions = departments.find((d) => d.name === dept)?.positions || [];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/materials/${material.id}`, { position: position || null, category: category || null });
      toast.success(position && category ? "Filed" : "Saved");
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not file"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="file-material-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FolderTree size={18} /> File "{material?.title}"</DialogTitle>
          <DialogDescription>Place this document under a Position and Category so the right people can see it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Department</Label>
            <Select value={dept} onValueChange={(v) => { setDept(v); setPosition(""); }}>
              <SelectTrigger data-testid="file-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>{departments.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Position</Label>
            <Select value={position} onValueChange={setPosition} disabled={!dept}>
              <SelectTrigger data-testid="file-position-select"><SelectValue placeholder={dept ? "Select position" : "Pick a department first"} /></SelectTrigger>
              <SelectContent>{deptPositions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="file-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => { setPosition(""); setCategory(""); setDept(""); }} data-testid="file-unfile-button">Move to Unfiled</Button>
          <Button disabled={busy} onClick={save} className="gap-2" data-testid="file-save-button">{busy && <Loader2 size={16} className="animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---- Upload / Link dialog with structured filing ---- */
function MaterialDialog({ mode, open, onOpenChange, departments, categories, settings, onDone }) {
  const isLink = mode === "link";
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dept, setDept] = useState("");
  const [position, setPosition] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const dropRef = useRef(null);

  const reset = () => { setFile(null); setUrl(""); setTitle(""); setDescription(""); setDept(""); setPosition(""); setCategory(""); setTags(""); };
  const deptPositions = departments.find((d) => d.name === dept)?.positions || [];

  const submit = async () => {
    if (isLink && (!url.trim() || !title.trim())) { toast.error("Link and title are required"); return; }
    if (!isLink && (!file || !title)) { toast.error("File and title are required"); return; }
    setBusy(true);
    try {
      if (isLink) {
        await api.post("/materials/link", {
          external_url: url.trim(), title: title.trim(), description,
          position: position || null, category: category || null,
          tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
        });
      } else {
        const fd = new FormData();
        fd.append("file", file); fd.append("title", title); fd.append("description", description);
        fd.append("position", position || ""); fd.append("category", category || ""); fd.append("tags", tags);
        await api.post("/materials", fd);
      }
      toast.success(isLink ? "Link added" : "Material uploaded");
      onDone(); onOpenChange(false); reset();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl" data-testid={isLink ? "link-dialog" : "upload-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{isLink ? <><Link2 size={18} /> Add Google Drive Link</> : <><FilePlus2 size={18} /> Upload Material</>}</DialogTitle>
          <DialogDescription>Optionally file it under a Position + Category now, or leave it Unfiled to place later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isLink ? (
            <div className="space-y-2"><Label>Google Drive URL *</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" data-testid="link-url-input" /></div>
          ) : (
            <div ref={dropRef} onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) { setFile(e.dataTransfer.files[0]); if (!title) setTitle(e.dataTransfer.files[0].name.replace(/\.[^.]+$/, "")); } }}
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => document.getElementById("upload-file-input").click()} data-testid="upload-dropzone">
              <Upload className="mx-auto mb-2 text-muted-foreground" size={28} />
              {file ? <p className="font-medium">{file.name}</p> : <p className="text-sm text-muted-foreground">Drag & drop a file here, or click to browse</p>}
              <p className="text-[11px] text-muted-foreground mt-1">PDF, DOCX, XLSX, PPTX, images, MP4/MOV · up to {settings?.max_upload_mb || 100}MB</p>
              <input id="upload-file-input" type="file" className="hidden" data-testid="upload-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, "")); } }} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={isLink ? "link-title-input" : "upload-title-input"} /></div>
            <div className="space-y-2"><Label>Tags (comma-separated)</Label><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="compliance, pos" data-testid={isLink ? "link-tags-input" : "upload-tags-input"} /></div>
            <div className="sm:col-span-2 space-y-2"><Label>Description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid={isLink ? "link-description-input" : "upload-description-input"} /></div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={dept} onValueChange={(v) => { setDept(v); setPosition(""); }}>
                <SelectTrigger data-testid="material-department-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Select value={position} onValueChange={setPosition} disabled={!dept}>
                <SelectTrigger data-testid="material-position-select"><SelectValue placeholder={dept ? "Select position" : "Unfiled"} /></SelectTrigger>
                <SelectContent>{deptPositions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="material-category-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} onClick={submit} className="gap-2" data-testid={isLink ? "link-submit-button" : "upload-submit-button"}>
            {busy && <Loader2 size={16} className="animate-spin" />} {isLink ? "Add Link" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---- Manage departments & positions ---- */
function StructureDialog({ open, onOpenChange, departments, onDone }) {
  const [draft, setDraft] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { if (open) setDraft(JSON.parse(JSON.stringify(departments || []))); }, [open]); // eslint-disable-line

  const setDeptName = (i, name) => setDraft((d) => d.map((x, idx) => idx === i ? { ...x, name } : x));
  const addDept = () => { const n = newDept.trim(); if (!n) return; setDraft((d) => [...d, { name: n, positions: [] }]); setNewDept(""); };
  const removeDept = (i) => setDraft((d) => d.filter((_, idx) => idx !== i));
  const addPos = (i, val) => { const v = (val || "").trim(); if (!v) return; setDraft((d) => d.map((x, idx) => idx === i ? { ...x, positions: [...x.positions, v] } : x)); };
  const removePos = (i, p) => setDraft((d) => d.map((x, idx) => idx === i ? { ...x, positions: x.positions.filter((q) => q !== p) } : x));

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", { departments: draft.map((d) => ({ name: d.name.trim(), positions: d.positions })).filter((d) => d.name) }); toast.success("Structure saved"); onDone(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="structure-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Settings2 size={18} /> Manage Structure</DialogTitle>
          <DialogDescription>Departments and their positions. Categories (Onboarding, SOPs, Training Packets) are fixed under every position.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {draft.map((d, i) => (
            <Card key={i} className="p-4 space-y-3" data-testid={`structure-dept-${i}`}>
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-accent shrink-0" />
                <Input value={d.name} onChange={(e) => setDeptName(i, e.target.value)} className="font-medium" data-testid={`structure-dept-name-${i}`} />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeDept(i)} data-testid={`structure-remove-dept-${i}`}><Trash2 size={14} /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5 pl-6">
                {d.positions.map((p) => (
                  <Badge key={p} variant="secondary" className="gap-1 pr-1">{p}
                    <button onClick={() => removePos(i, p)} className="ml-0.5 hover:text-destructive" data-testid={`structure-remove-pos-${i}-${p}`}><X size={11} /></button>
                  </Badge>
                ))}
                <AddPosInput onAdd={(v) => addPos(i, v)} idx={i} />
              </div>
            </Card>
          ))}
          <div className="flex gap-2">
            <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="New department name" data-testid="structure-new-dept-input" onKeyDown={(e) => e.key === "Enter" && addDept()} />
            <Button variant="outline" className="gap-1 shrink-0" onClick={addDept} data-testid="structure-add-dept-button"><Plus size={15} /> Add</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} onClick={save} className="gap-2" data-testid="structure-save-button">{busy && <Loader2 size={16} className="animate-spin" />} Save Structure</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPosInput({ onAdd }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-1">
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Add position" className="h-7 w-36 text-xs" data-testid="structure-add-pos-input"
        onKeyDown={(e) => { if (e.key === "Enter") { onAdd(v); setV(""); } }} />
    </div>
  );
}
